import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { SupporterCompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/SupporterCompanyRegistrationProfileInfo';
import { SupporterCompanyDetailQuery } from 'interfaceAdapter/queries/SupporterCompanyDetail';
import { SupporterCompanyEditRepository } from 'interfaceAdapter/repositories/SupporterCompanyEdit';
import { useLoading } from 'application/contexts/useLoading';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { areaToPrefecturesMap } from 'domain/valueObjects/Area/Area';
import { IIndustry } from 'domain/entities/Industry/Industry';

const schema = yup
    .object({
        name: yup.string().required(),
        company_code: yup
            .string()
            .nullable()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です')
            .transform((value) => value || null)
            .default(null),
        area: yup.string().required(),
        prefecture: yup.string().required(),
        number_of_employees: yup.string().required(),
        revenue_scale: yup.string().required(),
        industry: yup.string().required(),
        icon_image: yup.string().nullable(),
        business_description: yup.string().required(),
        profit_level: yup.string().required(),
        net_assets_level: yup.string().required(),
        question: yup.string().nullable(),
        hp_url: yup.string().nullable(),
        fee_structure: yup.boolean().required(),
        timing_considerations: yup.boolean().required(),
        matching_potential: yup.boolean().required(),
        case_studies: yup.boolean().required(),
        process_flow: yup.boolean().required(),
        fee_structure_txt: yup.string().default('').max(1000),
        case_studies_txt: yup.string().default('').max(1000),
        timing_considerations_txt: yup.string().default('').max(1000),
        matching_potential_txt: yup.string().default('').max(1000),
        process_flow_txt: yup.string().default('').max(1000),
        is_sole_proprietor: yup.boolean().required(),
    })
    .required();

type CompanyForm = yup.InferType<typeof schema>;

export const useSupporterCompanyEdit = () => {
    const diContainer = useContext(diContainerContext);
    const supporterCompanyRegistrationProfileInfoQuery = diContainer.resolve(
        SupporterCompanyRegistrationProfileInfoQuery,
    );
    const supporterCompanyDetailQuery = diContainer.resolve(SupporterCompanyDetailQuery);
    const supporterCompanyEditRepository = diContainer.resolve(SupporterCompanyEditRepository);
    const { loading, setLoading } = useLoading();
    const navigate = useNavigate();
    const params = useParams<{ companyId: string; type: 'seller' | 'buyer' }>();

    const [companyData, setCompanyData] = useState<any>(null);
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [prefectureChoices, setPrefectureChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<IIndustry[]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [filteredPrefectureChoices, setFilteredPrefectureChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackBarMessage, setSnackbarMessage] = useState('');
    const [editing, setEditing] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm<CompanyForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    const selectedArea = useWatch({
        control,
        name: 'area',
    });

    useEffect(() => {
        if (selectedArea) {
            const filtered = prefectureChoices.filter((prefecture) =>
                areaToPrefecturesMap[selectedArea].includes(prefecture[0]),
            );
            setFilteredPrefectureChoices(filtered);
        } else {
            setFilteredPrefectureChoices([]);
        }
    }, [selectedArea, prefectureChoices]);

    // 企業詳細情報を取得
    useEffect(() => {
        const fetchCompanyData = async () => {
            if (!params.companyId || params.type !== 'seller') {
                setError('売り手企業のみ編集可能です');
                return;
            }

            setLoading(true);
            try {
                const data = await supporterCompanyDetailQuery.fetchCompanyDetail(Number(params.companyId));
                setCompanyData(data);

                // フォームにデータを設定
                setValue('name', data.name || '');
                setValue('company_code', data.company_code || null);
                setValue('number_of_employees', data.number_of_employees || '');
                setValue('revenue_scale', data.revenue_scale || '');
                setValue('icon_image', data.icon_image || null);
                setValue('business_description', data.business_description || '');
                setValue('question', data.question || null);
                setValue('hp_url', data.hp_url || null);
                setValue('area', data.area || '');
                setValue('prefecture', data.prefecture || '');
                setValue('profit_level', data.profit_level || '');
                setValue('net_assets_level', data.net_assets_level || '');
                setValue('fee_structure', !!data.fee_structure);
                setValue('fee_structure_txt', data.fee_structure_txt || '');
                setValue('case_studies', !!data.case_studies);
                setValue('case_studies_txt', data.case_studies_txt || '');
                setValue('matching_potential', !!data.matching_potential);
                setValue('matching_potential_txt', data.matching_potential_txt || '');
                setValue('timing_considerations', !!data.timing_considerations);
                setValue('timing_considerations_txt', data.timing_considerations_txt || '');
                setValue('process_flow', !!data.process_flow);
                setValue('process_flow_txt', data.process_flow_txt || '');
                setValue('is_sole_proprietor', !!data.is_sole_proprietor);
            } catch (err) {
                console.error('企業情報の取得に失敗しました:', err);
                setError(err instanceof Error ? err.message : '企業情報の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchCompanyData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.companyId, params.type]);

    // プロフィール情報の選択肢を取得
    useEffect(() => {
        const fetchProfileInfo = async () => {
            try {
                const data = await supporterCompanyRegistrationProfileInfoQuery.fetch();
                setAreaChoices(data.area_choices);
                setPrefectureChoices(data.prefecture_choices);
                setEmployeeChoices(data.employee_choices);
                setRevenueChoices(data.revenue_choices);
                setIndustryChoices(data.industries);
                setProfitLevelChoices(data.profit_level_choices);
                setNetAssetsLevelChoices(data.net_assets_level_choices);
            } catch (err) {
                console.error('プロフィール情報の取得に失敗しました:', err);
            }
        };
        fetchProfileInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 業種の設定（industryChoicesが読み込まれた後）
    useEffect(() => {
        if (companyData && industryChoices.length > 0 && companyData.industry) {
            const industry = industryChoices.find((ind) => ind.name === companyData.industry);
            if (industry) {
                setValue('industry', industry.id.toString());
            }
        }
    }, [companyData, industryChoices, setValue]);

    const submit = handleSubmit(async (data) => {
        if (!params.companyId) {
            setSnackbarMessage('企業IDが指定されていません。');
            setShowSnackbar(true);
            return;
        }

        setLoading(true);
        try {
            await supporterCompanyEditRepository.editProfile({
                company_id: Number(params.companyId),
                ...data,
            });
            setSnackbarMessage('企業プロフィールを更新しました。');
            setShowSnackbar(true);
            setTimeout(() => navigate('/supporter/dashboard/'), 1500);
        } catch (err) {
            setSnackbarMessage(err instanceof Error ? err.message : '企業プロフィールの更新に失敗しました。');
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    });

    const onCompanySelect = (company: CompanySearchResult) => {
        setValue('name', company.name);
        setValue('company_code', company.number);
    };

    // userオブジェクトを模擬（CompanyEditFormで必要）
    const user = companyData
        ? {
              company: companyData,
          }
        : null;

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        filteredPrefectureChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        editing,
        user,
        snackBarMessage,
        setEditing,
        setShowSnackbar,
        submit,
        setValue,
        watch,
        onCompanySelect,
        error,
        companyType: params.type,
    };
};
